import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import {
  CHECKOUT_REQUEST_SESSION_KEY,
  IS_CHECKOUT_CREATED_SESSION_KEY,
  isRenderingForMobileApp,
} from 'src/constants';
import { constants } from '../../../app.constants';
import { GiftCardV2, Recipient } from '~models';
import { GiftCardsV2Service, LoaderService, TrxService, ZipYiService } from '~services';
import { environment } from '../../../../environments/environment';
import { ValidationService } from 'src/app/services/validation/validation.service';
import { GiphyService } from 'src/app/services/giphy/giphy.service';
import { ZipYiContext } from '~services/biocatch/zip-yi-context.enum';
import { CheckoutEvent, CheckoutRequest, GiftCardGiftingRequest } from '~models/checkout.model';
import { Subject } from 'rxjs';
import { SelectOptionV2Service } from 'src/app/shared-data';
import moment from 'moment';
import { FeatureFlagService } from '~services/feature-flag/feature-flag.service';
import { Section } from '~services/trx/events';

@Component({
  selector: 'app-checkout-view-v2',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
  standalone: false,
})
export class CheckoutViewV2Component implements OnInit, OnDestroy {
  public recipient: Recipient = new Recipient();
  public appPrefix = environment.appPrefix;
  public isMobileRender = isRenderingForMobileApp;
  public buyType = constants.BUY_TYPES.UNSELECTED;
  public isLoading = false;
  public giftCard: GiftCardV2;
  public giftCards: GiftCardV2[] = [];
  public browseGiftCards: GiftCardV2[] = [];

  private refreshInterval: number;
  private destroy$ = new Subject<boolean>();

  constructor(
    private ngActivatedRoute: ActivatedRoute,
    private ngRouter: Router,
    private giftCardV2Service: GiftCardsV2Service,
    private loaderService: LoaderService,
    public selectOptionV2Service: SelectOptionV2Service,
    private titleService: Title,
    public validationService: ValidationService,
    public giphyService: GiphyService,
    private zipYiService: ZipYiService,
    private featureFlagService: FeatureFlagService,
    private trxService: TrxService,
  ) {}

  ngOnDestroy(): void {
    clearInterval(this.refreshInterval);
    this.destroy$.next(true);
  }

  async ngOnInit() {
    this.loaderService.visible$.subscribe((visible) => {
      this.isLoading = visible;
    });
    this.loaderService.show("We're fetching your Gift Card.");
    await this.initGiftCard();
    this.updatePageTitle();
    this.loaderService.hide();
    this.zipYiService.setContext(ZipYiContext.CheckoutPage);
    this.trxService.sectionStartEvent(Section.GiftcardDetails);
    this.setupAutomaticRefresh();
    this.setupOnParamsChange();
    this.initGiftCards();
  }

  private async initGiftCard() {
    try {
      this.giftCard = await this.giftCardV2Service.getCard(this.id);
    } catch (error) {
      window.zbm?.reportError(error);
      this.ngRouter.navigate(['/']);
    }
  }

  private async initGiftCards() {
    try {
      const [giftCards, featuredIds] = await Promise.all([
        this.giftCardV2Service.getCards(),
        this.giftCardV2Service.getFeaturedCards(),
      ]);
      this.giftCards = giftCards
        .map((giftCard) => {
          return {
            ...giftCard,
            featured: featuredIds.includes(giftCard.id),
          } as GiftCardV2;
        })
        .sort((gc1, gc2) => Number(gc2.featured) - Number(gc1.featured));
    } catch (error) {
      this.giftCards = [];
    }
    this.browseGiftCards = this.giftCards
      .filter((giftCard) => giftCard.id !== this.giftCard.id)
      .slice(0, 6);
  }

  private async setupAutomaticRefresh() {
    this.refreshInterval = setInterval(() => {
      this.initGiftCard();
    }, 10000);
  }

  private setupOnParamsChange() {
    this.ngActivatedRoute.params.subscribe((params) => {
      if (params.id === this.giftCard.id) {
        return;
      }

      if (this.featureFlagService.isShopCentralEnabled) {
        (async () => {
          await this.initGiftCard();
        })();

        this.browseGiftCards = this.giftCards
          .filter((giftCard) => giftCard.id !== params.id)
          .slice(0, 6);
      } else {
        this.giftCard = this.giftCards.find((giftCard) => giftCard.id === params.id);
        this.browseGiftCards = this.giftCards
          .filter((giftCard) => giftCard.id !== this.giftCard.id)
          .slice(0, 6);
      }

      this.updatePageTitle();
      this.buyType = constants.BUY_TYPES.UNSELECTED;
    });
  }

  async initiateCheckout(event?: CheckoutEvent) {
    const redirect_uri = new URL(`${window.location.origin}/au/giftcards/finalise`);
    const params = new URLSearchParams({
      productId: this.giftCard.id,
    });

    if (this.simulatePending) {
      params.append('simulatePending', 'true');
    }

    redirect_uri.search = params.toString();

    const checkoutRequest: CheckoutRequest = {
      price: this.selectOptionV2Service.selectedDenomination,
      cardName: this.giftCard.brand,
      cardId: this.giftCard.id,
      imageUri: this.giftCard.image,
      vendor: this.giftCard.vendor,
      redirectUri: redirect_uri.href,
      merchantId: this.giftCard.merchantId,
    };

    if (event?.isGifting) {
      const giftingRequest: GiftCardGiftingRequest = {
        recipientEmail: this.validationService.friendForm.friendEmail.toLowerCase(),
        recipientEmailVerification:
          this.validationService.friendForm.friendEmailConfirm.toLowerCase(),
        recipientName: this.sanitiseInput(this.validationService.friendForm.friendName),
        message: this.sanitiseInput(this.validationService.friendForm.friendNote),
      };

      const deliveryDate = this.getDeliveryDate();
      if (deliveryDate) {
        giftingRequest.deliveryDate = deliveryDate;
      }

      if (this.giphyService.imageGiphy !== '') {
        giftingRequest.messageImageURL = this.giphyService.imageGiphy.images.original.url;
      }

      checkoutRequest.gifting = giftingRequest;
    }

    sessionStorage.removeItem(IS_CHECKOUT_CREATED_SESSION_KEY);
    sessionStorage.setItem(CHECKOUT_REQUEST_SESSION_KEY, JSON.stringify(checkoutRequest));

    this.trxService.sectionSuccessEvent(
      event?.isGifting ? 'buy for friend' : 'buy for myself',
      checkoutRequest.price,
    );
    this.ngRouter.navigateByUrl(`/checkout/${this.id}/create`);
  }

  private sanitiseInput(input?: string) {
    return input
      ?.replace(/\p{Emoji_Presentation}/gu, '')
      ?.replace(/\s+/g, ' ')
      ?.trim();
  }

  continueShopping() {
    const { utm_medium } = this.ngActivatedRoute.snapshot.queryParams;
    utm_medium && utm_medium === 'StoreDirectory'
      ? (window.location.href = `${window.location.origin}/shop`)
      : this.ngRouter.navigate(['']);
  }

  back() {
    if (this.buyType !== constants.BUY_TYPES.UNSELECTED) {
      this.buyType = constants.BUY_TYPES.UNSELECTED;
    } else {
      this.continueShopping();
    }
  }

  public updatePageTitle() {
    this.titleService.setTitle(
      `Buy ${this.giftCard.brand} Gift Card & Voucher Online with Zip Pay & Zip Money`,
    );
  }

  public getDeliveryDate(): string | null {
    if (this.simulateSchedule) {
      this.validationService.deliveryDate = moment();
      this.validationService.setDeliveryDateData();
      return this.validationService.deliveryDate;
    }

    if (this.validationService.deliveryDate !== '') {
      return this.validationService.deliveryDate;
    }

    return null;
  }

  public get id() {
    return this.ngActivatedRoute.snapshot.params.id;
  }

  public get orderId() {
    return this.ngActivatedRoute.snapshot.queryParams.orderId;
  }

  public get isCaltexCard() {
    return this.giftCard.id === 'CALTEX_STARCASH_DIGITAL';
  }

  public get simulateSchedule() {
    return this.ngActivatedRoute.snapshot.queryParams.simulateSchedule === 'true';
  }

  public get simulatePending() {
    return this.ngActivatedRoute.snapshot.queryParams.simulatePending === 'true';
  }
}
